/**
 * Bell del Header con badge + dropdown funcional — Sprint 11 v2
 * S11-V2-15.
 *
 * Se autogestiona: consume `/vecino/notificaciones/count-no-leidas`
 * cada 60s + on focus de la pestaña para mantener el badge fresco, y
 * carga la lista completa solo cuando el usuario abre el dropdown
 * (`/vecino/notificaciones?leidas=false&limit=10`).
 *
 * Comportamiento:
 * - Click en bell → toggle dropdown. Al abrir, carga la lista.
 * - Click en item → marca-leida + navega a `enlace_destino` si existe.
 * - "Marcar todas como leídas" → marca-todas-leidas + cierra dropdown.
 *
 * Render del badge:
 * - count === 0 → no se muestra (svg sin badge).
 * - count entre 1 y 9 → badge con el número.
 * - count > 9 → badge con "9+".
 *
 * Posicionamiento (S21): el panel se renderiza en un PORTAL a `document.body`
 * vía `useMenuAnclado` para escapar del `overflow-hidden` del shell (que lo
 * recortaba en páginas cortas). En desktop es un dropdown con posición fija
 * anclado a la campana; en mobile (<md) es una hoja inferior fija con scrim.
 * El modo se captura al abrir (el hook cierra ante resize, así no queda
 * desfasado).
 *
 * Si la API falla, el bell sigue render-able sin badge (degradación elegante).
 */
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  contarNoLeidas,
  listarNotificaciones,
  marcarLeida,
  marcarTodasLeidas,
  type NotificacionVecino,
} from '../../lib/api/notificaciones';
import { useMenuAnclado } from './useMenuAnclado';

const POLL_INTERVAL_MS = 60_000;
const BREAKPOINT_MD = 768;

function tiempoRelativo(isoString: string): string {
  const fecha = new Date(isoString);
  const ahora = Date.now();
  const diffMs = ahora - fecha.getTime();
  const min = Math.floor(diffMs / 60_000);
  if (min < 1) return 'hace unos segundos';
  if (min < 60) return `hace ${min} min`;
  const horas = Math.floor(min / 60);
  if (horas < 24) return `hace ${horas} h`;
  const dias = Math.floor(horas / 24);
  if (dias === 1) return 'ayer';
  if (dias < 7) return `hace ${dias} días`;
  return fecha.toLocaleDateString('es-CL');
}

export function Bell() {
  const [count, setCount] = useState(0);
  const [items, setItems] = useState<NotificacionVecino[]>([]);
  const [cargandoLista, setCargandoLista] = useState(false);
  const [esDesktop, setEsDesktop] = useState(false);
  const navigate = useNavigate();
  const { abierto, coords, botonRef, menuRef, alternar, cerrar } =
    useMenuAnclado();

  // Polling del badge: count cada 60s + on focus de la pestaña.
  useEffect(() => {
    let cancelado = false;

    async function refrescarCount() {
      try {
        const { count } = await contarNoLeidas();
        if (!cancelado) setCount(count);
      } catch {
        // Degradacion elegante: no rompemos UI si falla.
      }
    }

    refrescarCount();
    const interval = setInterval(refrescarCount, POLL_INTERVAL_MS);
    window.addEventListener('focus', refrescarCount);

    return () => {
      cancelado = true;
      clearInterval(interval);
      window.removeEventListener('focus', refrescarCount);
    };
  }, []);

  const cargarLista = useCallback(async () => {
    setCargandoLista(true);
    try {
      const lista = await listarNotificaciones({ leidas: false, limit: 10 });
      setItems(lista);
    } catch {
      setItems([]);
    } finally {
      setCargandoLista(false);
    }
  }, []);

  // Al abrir: captura modo (desktop/mobile) y carga la lista.
  useEffect(() => {
    if (!abierto) return;
    setEsDesktop(window.innerWidth >= BREAKPOINT_MD);
    cargarLista();
  }, [abierto, cargarLista]);

  const handleClickItem = useCallback(
    async (notif: NotificacionVecino) => {
      try {
        await marcarLeida(notif.id);
      } catch {
        // Sigue navegando aunque falle el marcar — UX > correctness.
      }
      setItems((prev) => prev.filter((n) => n.id !== notif.id));
      setCount((c) => Math.max(0, c - 1));
      cerrar();
      if (notif.enlace_destino) {
        navigate(notif.enlace_destino);
      }
    },
    [cerrar, navigate],
  );

  const handleMarcarTodas = useCallback(async () => {
    try {
      await marcarTodasLeidas();
    } catch {
      return;
    }
    setItems([]);
    setCount(0);
    cerrar();
  }, [cerrar]);

  const badgeText = count > 9 ? '9+' : count > 0 ? String(count) : null;

  return (
    <div className="flex items-center pl-3 border-l-[0.5px] border-white/25">
      <button
        ref={botonRef}
        type="button"
        onClick={alternar}
        aria-label="Notificaciones"
        aria-expanded={abierto}
        className="relative flex items-center cursor-pointer focus:outline-none focus:ring-2 focus:ring-white/40 rounded-sm p-1"
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="text-white"
          aria-hidden="true"
        >
          <path d="M6 8 C6 5 8 3 12 3 C16 3 18 5 18 8 V13 L20 16 H4 L6 13 Z" />
          <path d="M10 19 C10 20.5 11 21 12 21 C13 21 14 20.5 14 19" />
        </svg>
        {badgeText && (
          <span
            className="absolute -top-0.5 -right-0.5 bg-bg-page text-white text-[9px] font-medium px-[5px] py-[1px] rounded-full min-w-[14px] text-center border-[1.5px] border-orange"
            aria-label={`${count} notificaciones no leídas`}
          >
            {badgeText}
          </span>
        )}
      </button>

      {abierto &&
        coords &&
        createPortal(
          <>
            {/* Scrim mobile detrás de la hoja inferior; click cierra. */}
            {!esDesktop && (
              <div
                className="fixed inset-0 bg-black/40 z-40"
                aria-hidden="true"
                onClick={cerrar}
              />
            )}
            <div
              ref={menuRef}
              role="dialog"
              aria-label="Notificaciones recientes"
              style={
                esDesktop
                  ? { position: 'fixed', top: coords.top, right: coords.right }
                  : { paddingBottom: 'env(safe-area-inset-bottom, 0)' }
              }
              className={
                esDesktop
                  ? 'w-80 rounded-md bg-bg-card border-[0.5px] border-white/15 shadow-2xl z-50 overflow-hidden'
                  : 'fixed inset-x-0 bottom-0 max-h-[80vh] rounded-t-lg bg-bg-card border-[0.5px] border-white/15 shadow-2xl z-50 overflow-hidden'
              }
            >
              {/* Drag handle visual solo en mobile (afordancia de hoja). */}
              {!esDesktop && (
                <div className="flex justify-center pt-2 pb-1">
                  <span
                    className="w-10 h-1 rounded-full bg-white/20"
                    aria-hidden="true"
                  />
                </div>
              )}
              <div className="px-4 py-3 border-b-[0.5px] border-white/[0.08]">
                <h3 className="text-base font-medium text-text-primary">
                  Notificaciones
                </h3>
              </div>

              <div className="max-h-96 overflow-y-auto">
                {cargandoLista && (
                  <p className="text-sm text-text-muted px-4 py-6 text-center">
                    Cargando…
                  </p>
                )}

                {!cargandoLista && items.length === 0 && (
                  <p className="text-sm text-text-muted px-4 py-6 text-center">
                    No tienes notificaciones nuevas.
                  </p>
                )}

                {!cargandoLista &&
                  items.map((notif) => (
                    <button
                      key={notif.id}
                      type="button"
                      onClick={() => handleClickItem(notif)}
                      className="w-full text-left px-4 py-3 border-b-[0.5px] border-white/[0.08] hover:bg-bg-elevated transition-colors duration-base focus:outline-none focus:bg-bg-elevated"
                    >
                      <div className="flex items-start gap-2">
                        <span
                          className="mt-1.5 w-2 h-2 rounded-full bg-orange shrink-0"
                          aria-hidden="true"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-text-primary truncate">
                            {notif.titulo}
                          </div>
                          <div className="text-xs text-text-secondary mt-0.5 line-clamp-2">
                            {notif.mensaje}
                          </div>
                          <div className="text-[10px] text-text-muted mt-1 uppercase tracking-wide">
                            {tiempoRelativo(notif.creada_at)}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
              </div>

              <div className="px-4 py-2 border-t-[0.5px] border-white/[0.08] flex items-center justify-between text-xs">
                <button
                  type="button"
                  onClick={handleMarcarTodas}
                  disabled={count === 0}
                  className="text-orange hover:underline disabled:text-text-muted disabled:no-underline disabled:cursor-not-allowed"
                >
                  Marcar todas como leídas
                </button>
                <span className="text-text-muted">{count} sin leer</span>
              </div>
            </div>
          </>,
          document.body,
        )}
    </div>
  );
}
